import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from "@mui/material"
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline"
import type { SelectChangeEvent } from "@mui/material/Select"
import { useMonthlyReports } from "@/features/profit-calculators/calculator-shared/hooks/useMonthlyReports"
import MonthlyReportsTable from "@/features/profit-calculators/calculator-shared/components/MonthlyReportsTable"
import PlatformIcon from "@/features/profit-calculators/calculator-shared/components/PlatformIcon"
import CalculatorTutorialDialog from "@/features/profit-calculators/calculator-shared/components/CalculatorTutorialDialog"
import { isFutureMonthKey } from "@/features/profit-calculators/calculator-shared/utils/reportMonthUtils"

import MonthlyPerformanceChart from "./MonthlyPerformanceChart"

interface Props {
  platform?: "meesho" | "amazon"
  section?: "profit" | "chart"
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const EARLIEST_REPORT_YEAR = 2023

const buildYearMonthOptions = (year: number) =>
  MONTH_NAMES.map((monthName, index) => ({
    monthKey: `${year}-${String(index + 1).padStart(2, "0")}`,
    label: `${monthName} ${year}`
  })).filter(option => !isFutureMonthKey(option.monthKey))

const MonthlyReportsPage: React.FC<Props> = ({ platform = "meesho", section = "profit" }) => {
  const navigate = useNavigate()
  const currentYear = new Date().getUTCFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const { reports, loading, error } = useMonthlyReports(platform, selectedYear)
  const [selectedMonth, setSelectedMonth] = useState("")
  const [tutorialOpen, setTutorialOpen] = useState(false)

  const monthOptions = useMemo(() => buildYearMonthOptions(selectedYear), [selectedYear])
  const yearOptions = useMemo(
    () => Array.from({ length: currentYear - EARLIEST_REPORT_YEAR + 1 }, (_, index) => currentYear - index),
    [currentYear]
  )

  const handlePickMonth = (monthKey: string) => {
    if (isFutureMonthKey(monthKey)) return
    navigate(`/${platform}-calculator/${monthKey}/upload`)
  }

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const monthKey = event.target.value
    setSelectedMonth(monthKey)
    handlePickMonth(monthKey)
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 4 }}>
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PlatformIcon platform={platform} />
            <Typography variant="h5" fontWeight={800}>
              {platform === "amazon" ? "Amazon" : "Meesho"}{" "}
              {section === "chart" ? "Performance Chart" : "Profit Reports"}
            </Typography>
            {platform === "meesho" && (
              <IconButton
                aria-label="How to use Meesho Profit Calculator"
                title="How to use Meesho Profit Calculator"
                color="primary"
                size="small"
                onClick={() => setTutorialOpen(true)}>
                <PlayCircleOutlineIcon />
              </IconButton>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {section === "chart"
              ? "Review your monthly sales, profit and loss"
              : "Generate and review your monthly profit reports"}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignSelf={{ xs: "stretch", sm: "center" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="report-year-label">Year</InputLabel>
            <Select
              labelId="report-year-label"
              value={String(selectedYear)}
              label="Year"
              onChange={event => {
                setSelectedYear(Number(event.target.value))
                setSelectedMonth("")
              }}
              sx={{ borderRadius: 2 }}>
              {yearOptions.map(year => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {section === "profit" && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="generate-report-month-label" shrink>
                Generate Report
              </InputLabel>
              <Select
                labelId="generate-report-month-label"
                value={selectedMonth}
                label="Generate Report"
                displayEmpty
                onChange={handleMonthChange}
                renderValue={value =>
                  value ? (monthOptions.find(option => option.monthKey === value)?.label ?? value) : "Select month"
                }
                sx={{ borderRadius: 2 }}>
                <MenuItem value="" disabled>
                  Select month
                </MenuItem>
                {monthOptions.map(opt => (
                  <MenuItem key={opt.monthKey} value={opt.monthKey}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {section === "chart" && !loading && !error && (
        <MonthlyPerformanceChart reports={reports} year={selectedYear} platform={platform} />
      )}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <CircularProgress />
        </Stack>
      ) : section === "profit" ? (
        <MonthlyReportsTable
          platform={platform}
          reports={reports}
          monthOptions={monthOptions}
          onGenerate={handlePickMonth}
          onView={monthKey => navigate(`/${platform}-calculator/${monthKey}/report`)}
        />
      ) : null}
      <CalculatorTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </Box>
  )
}

export default MonthlyReportsPage
